import React, { useState, useEffect } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import {
  X, User, FileText, DollarSign, Briefcase, Mail, Phone,
  MapPin, Clock, CheckCircle, AlertCircle, ArrowLeft, ExternalLink
} from "lucide-react";

const ClientProfileModal = ({ clientId, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (clientId) fetchProfile();
  }, [clientId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/profile`, { withCredentials: true });
      setProfile(res.data);
    } catch (e) { toast.error("Failed to load client profile"); onClose(); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  if (!profile) return null;

  const fin = profile.financial_summary || {};

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="client-profile-modal">
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-3xl p-6 my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">{profile.name}</h2>
            <div className="flex flex-wrap items-center gap-3 text-[#A0AAB2] text-sm mt-1">
              <span className="flex items-center space-x-1"><Mail size={14} /><span>{profile.email}</span></span>
              <span className="flex items-center space-x-1"><Phone size={14} /><span>{profile.phone}</span></span>
              {profile.address && <span className="flex items-center space-x-1"><MapPin size={14} /><span>{profile.address}</span></span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
        </div>

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#1F2027] rounded-lg p-4">
            <span className="text-[#A0AAB2] text-xs">Total Invoiced</span>
            <p className="text-white font-bold text-lg" data-testid="client-total-invoiced">${fin.total_invoiced?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-[#1F2027] rounded-lg p-4">
            <span className="text-[#A0AAB2] text-xs">Total Paid</span>
            <p className="text-green-400 font-bold text-lg" data-testid="client-total-paid">${fin.total_paid?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-[#1F2027] rounded-lg p-4">
            <span className="text-[#A0AAB2] text-xs">Outstanding</span>
            <p className={`font-bold text-lg ${fin.outstanding_balance > 0 ? "text-yellow-400" : "text-green-400"}`} data-testid="client-outstanding">
              ${fin.outstanding_balance?.toLocaleString(undefined, {minimumFractionDigits: 2})}
            </p>
          </div>
          <div className="bg-[#1F2027] rounded-lg p-4">
            <span className="text-[#A0AAB2] text-xs">Jobs</span>
            <p className="text-white font-bold text-lg">{fin.total_jobs} <span className="text-sm font-normal text-[#A0AAB2]">({fin.active_jobs} active)</span></p>
          </div>
        </div>

        {/* Quotes Section */}
        {profile.quotes?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-base font-semibold text-white mb-3 flex items-center space-x-2">
              <FileText size={16} className="text-[#FF6B35]" /><span>Quotes ({profile.quotes.length})</span>
            </h3>
            <div className="space-y-2">
              {profile.quotes.map(q => (
                <div key={q.id} onClick={() => window.location.href = `/admin/quotes/${q.id}`}
                  className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3 flex items-center justify-between cursor-pointer hover:border-[#FF6B35]/50 transition-all"
                  data-testid={`client-quote-${q.id}`}>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white text-sm font-medium">{q.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        q.status === "draft" ? "bg-gray-500/20 text-gray-400" :
                        q.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                        q.status === "approved" ? "bg-green-500/20 text-green-400" :
                        q.status === "signed" ? "bg-emerald-500/20 text-emerald-400" :
                        q.status === "invoiced" ? "bg-purple-500/20 text-purple-400" :
                        "bg-red-500/20 text-red-400"
                      }`}>{q.status}</span>
                    </div>
                    <p className="text-[#A0AAB2] text-xs">{q.quote_number}</p>
                  </div>
                  <span className="text-[#FF6B35] font-bold">${q.total_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invoices Section */}
        {profile.invoices?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-base font-semibold text-white mb-3 flex items-center space-x-2">
              <DollarSign size={16} className="text-green-400" /><span>Invoices ({profile.invoices.length})</span>
            </h3>
            <div className="space-y-2">
              {profile.invoices.map(inv => (
                <div key={inv.id} className={`bg-[#1F2027] border rounded-lg p-3 flex items-center justify-between ${inv.status === "paid" ? "border-green-500/30" : "border-[#2A2B32]"}`}>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white text-sm font-medium">{inv.invoice_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        inv.status === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
                      }`}>{inv.status}</span>
                    </div>
                    <p className="text-[#A0AAB2] text-xs">Due: {inv.due_date}</p>
                  </div>
                  <span className={`font-bold ${inv.status === "paid" ? "text-green-400" : "text-yellow-400"}`}>
                    ${inv.amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Jobs Section */}
        {profile.jobs?.length > 0 && (
          <div>
            <h3 className="text-base font-semibold text-white mb-3 flex items-center space-x-2">
              <Briefcase size={16} className="text-blue-400" /><span>Jobs ({profile.jobs.length})</span>
            </h3>
            <div className="space-y-2">
              {profile.jobs.map(j => (
                <div key={j.id} className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3 flex items-center justify-between cursor-pointer hover:border-[#FF6B35]/50 transition-all"
                  data-testid={`client-job-${j.id}`}>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white text-sm font-medium">{j.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        j.status === "completed" ? "bg-green-500/20 text-green-400" :
                        j.status === "in_progress" ? "bg-yellow-500/20 text-yellow-400" :
                        j.status === "scheduled" ? "bg-blue-500/20 text-blue-400" :
                        "bg-gray-500/20 text-gray-400"
                      }`}>{j.status}</span>
                    </div>
                    {j.scheduled_date && <p className="text-[#A0AAB2] text-xs">{j.scheduled_date}</p>}
                  </div>
                  <span className="text-[#FF6B35] font-bold">${(j.total_price || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty states */}
        {(!profile.quotes?.length && !profile.invoices?.length && !profile.jobs?.length) && (
          <p className="text-[#A0AAB2] text-center py-8">No quotes, invoices, or jobs yet for this client.</p>
        )}
      </div>
    </div>
  );
};

export default ClientProfileModal;
